import { UserProfile } from '../../../types/commons';
import { ChangePasswordRequest } from '../../../types/profile';
import { UpdateUserRequest } from '../../../types/user';
import { parseUserRole, UserRole } from '../../../types/user-role';

/**
 * The raw value of the profile form — every control, including the disabled
 * ones (`getRawValue()`, never `.value`, which drops them).
 *
 * Written out explicitly rather than derived from the component so these pure
 * helpers can live outside it. The component's form is checked against this
 * shape at the call sites, so a renamed control fails to compile here.
 */
export interface ProfileFormRaw {
  inputName: string | null;
  inputEmail: string | null;
  selectedCompany: string | null;
  selectedRole: string | null;
  inputCurrentPassword: string | null;
  inputNewPassword: string | null;
  inputConfirmPassword: string | null;
}

/** What the user actually changed, as read by the Save gate and the save chain. */
export interface ProfileChangeSet {
  profileChanged: boolean;
  passwordTouched: boolean;
  passwordChanged: boolean;
}

/**
 * The one baseline the role comparison is made against.
 *
 * Shared by {@link computeChangeSet} (did the role change?) and
 * {@link buildUpdateUserRequest} (should `role` go in the payload?). They used
 * to derive it separately — one read `profile.roles` while the other fell back
 * to `'User'` — so a profile whose `roles` held neither `'Admin'` nor `'User'`
 * (or was empty) produced a spurious `role: ''` key on every save that touched
 * any other field.
 *
 * Pure and DI-free by contract.
 */
export function baselineRole(profile: UserProfile | null): UserRole | '' {
  // `parseUserRole` returns null (never User) for an unrecognised role, so a
  // future or malformed role is left blank rather than mislabelled as User.
  return parseUserRole(profile?.roles?.[0]) ?? '';
}

/**
 * Single source of truth for "what did the user actually change?".
 *
 * The Save-button gate and the save chain (which of the two endpoints to call)
 * both read it, so the two can never drift.
 *
 * Three flags, not two, because the button and the chain ask different
 * questions of the password fields:
 *   - `passwordTouched` — ANY of the three filled, so the button lights up
 *     mid-entry and the incomplete-set validator can flag it.
 *   - `passwordChanged` — ALL three filled, i.e. a real submit.
 *
 * Pure and DI-free by contract.
 */
export function computeChangeSet(
  raw: ProfileFormRaw,
  profile: UserProfile | null
): ProfileChangeSet {
  if (!profile) {
    return { profileChanged: false, passwordTouched: false, passwordChanged: false };
  }

  const nameChanged =
    (raw.inputName ?? '').toString().trim() !== (profile.name ?? '').toString().trim();

  // Company is deliberately absent: it is never editable on My Profile (company
  // moves are SuperAdmin-only, in User Management), and since 2026-10-20 the
  // profile carries only the company NAME, no id to compare or send.
  const roleChanged = (raw.selectedRole ?? '') !== baselineRole(profile);

  const filled = [
    raw.inputCurrentPassword,
    raw.inputNewPassword,
    raw.inputConfirmPassword,
  ].filter((value) => !!value).length;

  return {
    profileChanged: nameChanged || roleChanged,
    passwordTouched: filled > 0,
    passwordChanged: filled === 3,
  };
}

/** The password-change payload. Pure and DI-free by contract. */
export function buildChangePasswordRequest(
  raw: ProfileFormRaw
): ChangePasswordRequest {
  return {
    currentPassword: raw.inputCurrentPassword ?? '',
    newPassword: raw.inputNewPassword ?? '',
    confirmPassword: raw.inputConfirmPassword ?? '',
  };
}

/**
 * Only the keys that actually changed, mirroring what the user-management
 * screen sends. No acting-user field is included in the request, and never a
 * `companyId` (see `computeChangeSet`).
 *
 * Pure and DI-free by contract.
 */
export function buildUpdateUserRequest(
  raw: ProfileFormRaw,
  profile: UserProfile | null
): UpdateUserRequest {
  const credentials: UpdateUserRequest = { id: profile?.id ?? '' };

  const name = (raw.inputName ?? '').toString().trim();
  if (name !== (profile?.name ?? '').toString().trim()) {
    credentials.name = name;
  }

  // Email is deliberately absent: it is read-only on My Profile for every role
  // (P.O., 2026-10-27), so it is never part of this request.
  const role = raw.selectedRole ?? '';
  if (role !== baselineRole(profile)) {
    credentials.role = role;
  }

  return credentials;
}
